import { UserContext } from "@Constants";
import SessionManager from "@Core/session";
import { IAssessmentDetails, IAssessmentInstanceDetails } from "@Models/IAssessmentDetails";
import { ITemplateField } from "@Models/IFeedbackTemplateDetails";
import { arrayToMapByKey, DateTime, hasCapability } from "@Utils";
import moment from "moment";
import { INITIAL_VALUES } from "./assessmentConstants";
import { IExtendedAssessmentInstanceDetails } from "./types";
import { IActivityDetails } from "@Models/IActivityDetails";
import { ITaskDetails } from "@Models/ITaskDetails";

export const extendAssessmentInstanceDetails = (
    assessmentInstance: IAssessmentInstanceDetails,
    owner: string,
    activitiesInstancesMap: Map<string, IActivityDetails>
): IExtendedAssessmentInstanceDetails => {
    const {
        endDate = "01/01/0001",
        name,
        lateSubmissions = 0,
        submissionDate,
        tasks,
        taskDetails,
    } = { ...assessmentInstance };

    const tasksMap = taskDetails
        ? arrayToMapByKey(taskDetails, (a) => a.taskId, true)
        : new Map<string, ITaskDetails>();

    const isBlankDate = DateTime(endDate).isBlankDate();
    const onTime =
        isBlankDate || +moment(submissionDate) < +moment(endDate, "DD/MM/YYYY").add(lateSubmissions || 0, "days");
    const taskIdArray: string[] = [];

    const tasksWithDetails = tasks?.map((task) => {
        const activityInstance = activitiesInstancesMap?.get(task.activityInstanceId) || {};
        const { activityLabel, creationDate } = activityInstance;
        const { quantity } = tasksMap?.get(task.taskId);
        taskIdArray.push(task.taskId);
        return {
            ...task,
            activityLabel,
            count: taskIdArray.filter((tia) => tia === task.taskId).length,
            creationDate,
            quantity,
        };
    });

    const extendedAssessmentInstanceDetails: IExtendedAssessmentInstanceDetails = {
        ...assessmentInstance,
        owner,
        onTime: onTime,
        tasks: tasksWithDetails,
        name: name,
    };

    return extendedAssessmentInstanceDetails;
};

export const getDefaultOptions = (type: string) => {
    switch (type) {
        case "text":
            return [];
        case "textarea":
            return [];
        case "numeric":
            return ["0", "100", "false"];
        case "boolean":
            return [];
        case "option":
            return [];
        case "likert":
            return ["Strongly disagree", "Disagree", "Neither agree nor disagree", "Agree", "Strongly agree"];
    }
    return null;
};

export const getIndex = (id: string, fields: ITemplateField[]) => fields?.findIndex((x) => x.id === id);

export const getSchema = (id: string, fields: ITemplateField[]) => fields?.find((x) => x.id === id);

export const checkCap = (capability: string): boolean => {
    if (!capability) return false;
    return hasCapability(capability, UserContext);
};

interface IPopulateInitialValues {
    assessment: IAssessmentDetails;
    assessorGroups: string[];
    groupIdsByUser: Map<string, Set<string>>;
    isReady: boolean;
    populateMissingOptions: () => Partial<Record<keyof IAssessmentDetails, boolean>>;
}

export const populateInitialValues = ({
    assessment,
    assessorGroups,
    groupIdsByUser,
    isReady,
    populateMissingOptions,
}: IPopulateInitialValues) => {
    if (isReady) return { ...INITIAL_VALUES, owner: SessionManager?.getServiceUser()?.userId }; // the current user is the default assessor
    const { description, endDate, feedbackTemplateId, lateSubmissions, name, owner, startDate, users } = assessment;

    const missingOptions = populateMissingOptions();

    // we prefix the users with the group in case a user is in multiple groups if the tree is still editable.
    const usersWithPrefix = [];
    const isPastStartDate = DateTime(assessment.startDate).isPast();

    users.forEach((user) => {
        if (isPastStartDate) return usersWithPrefix.push({ label: user.fullName });
        const userGroups =
            Array.from(groupIdsByUser.get(user.userId)).filter((groupId) => assessorGroups.includes(groupId)) || [];
        return userGroups.forEach((groupId) =>
            usersWithPrefix.push({ label: user.fullName, value: `${groupId}¦${user.userId}` })
        );
    });

    return {
        ...INITIAL_VALUES,
        assessmentId: assessment.assessmentId,
        description,
        endDate: moment(endDate) as never,
        feedbackTemplateId: !missingOptions.feedbackTemplateId ? feedbackTemplateId : null,
        lateSubmissions,
        name,
        owner: !missingOptions.owner ? owner : null,
        startDate: moment(startDate) as never,
        users: usersWithPrefix as never, // treeselect expects label and value
    };
};

export const getTutorialTip = (tab: string) => {
    switch (tab) {
        case "current":
            return (
                "This is an overview of all assessments that you are currently assessing, including the number " +
                "of tasks set, the template to be used for all submission feedback and whether the due date has passed. " +
                "Select an assessment from the list, to view user submissions or to update it."
            );
        case "new":
            return (
                "Create a new assessment from an existing template, " +
                "assign an assessor to manage it and then a group of users who will be assigned the assessment. " +
                "You can also set the number of days within which to accept late submissions."
            );
        case "templates":
            return (
                "Create or edit assessment feedback templates. Templates will only be usable within the " +
                "primary group that you select here. Add and remove the fields you want to use to provide " +
                "assessment feedback and hit the save button. The template will then be available to use " +
                "in any assessments you create."
            );
        case "edit":
            return (
                "Here you can edit an existing assessment. Note that once an assessment start date has already passed " +
                "you will no longer be allowed to change key information, including the template, tasks, start " +
                "and end dates, the assessor or the assigned users."
            );
        case "submissions":
            return (
                "View submissions for a given assessment here. Use the activity select to review individual " +
                "activities that were submitted and to read any completed feedback."
            );
        case "users":
            return (
                "Here you can see a list of the users to whom the assessment has been assigned. Use the " +
                "filters to display assessments with various statuses. Assigned means the user has not yet " +
                "submitted any activities for the assessment. Completed means the assessor has reviewed a submission " +
                "and completed the feedback."
            );
        case "assigned":
            return (
                "View all assessments that are currently assigned to you. Select an assessment and submit tasks to it. If " +
                "your assessor has disabled activity selection then task submissions will be pre-selected for you from your " +
                "recent candidate activities, to enforce exam conditions."
            );
        case "submitted":
            return (
                "View all assessments that you have already submitted to. These submissions have not yet been reviewed " +
                "and completed by your assessor."
            );
        case "completed":
            return (
                "View all assessments that have been submitted by you and then completed by your assessor. Select an " +
                "assessment from the list to review any feedback that you have received."
            );
        case "activity":
            return "Select from the available activities below and optionally select a date range. If you wish to view progression data then you must select only one activity type within the search field.";
        case "audiences":
            return "Audiences allow you to create groups of activities that can be associated with any license in the same product category. You can also add additional custom tags to an audience.";
        case "licenses":
            return "From here you can manage all licenses across the platform. Each license covers a single product, but you can assign multiple assets per license.";
        default:
            return "";
    }
};
